import { ColorV2, Text } from "@adoptaunabuelo/react-components";
import media from "styled-media-query";
import styled, { keyframes } from "styled-components";

interface Props {
	title: string;
	description: string;
	imageAlt: string;
	imageSrc: string;
	reverseOrder?: boolean;
	background?: string;
	copyPadding?: string;
	cardPadding?: string;
	imageWidth?: string;
	imageCompactWidth?: string;
	imageHeight?: string;
	imageMaxWidth?: string;
	imageMaxHeight?: string;
	imageCompactMaxHeight?: string;
	imageCompactMarginTop?: string;
	imageFit?: "contain" | "cover";
	imagePosition?: string;
	imageTransform?: string;
	imageMarginTop?: string;
	imageJustify?: "flex-start" | "center" | "flex-end";
	imageAlign?: "flex-start" | "center" | "flex-end";
	imageMarginBottom?: string;
	imageSidePadding?: string;
}

const revealFromBottom = keyframes`
	from {
		opacity: 0;
		transform: translateY(24px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const StepContent = styled.div`
	text-align: left;
	display: flex;
	width: 100%;
	max-width: 100%;
	height: 100%;
	min-height: 0;
	flex-direction: column;
	background: ${ColorV2.surface.invert};
	border-radius: inherit;
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
`;

const Card = styled.div<{ $padding: string; $background: string }>`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 100%;
	min-height: 100%;
	box-sizing: border-box;
	padding: ${(props) => props.$padding};
	background: ${(props) => props.$background};
	border-radius: inherit;
	overflow: hidden;
`;

const Copy = styled.div<{ $padding?: string }>`
	display: flex;
	gap: 8px;
	flex-direction: column;
	max-width: 520px;
	padding: ${(props) => props.$padding ?? "0"};
`;

const IllustrationWrap = styled.div<{
	$marginTop: string;
	$compactMarginTop?: string;
	$justify: NonNullable<Props["imageJustify"]>;
	$align: NonNullable<Props["imageAlign"]>;
	$marginBottom: string;
	$sidePadding: string;
}>`
	display: flex;
	flex: 1;
	min-height: 0;
	width: 100%;
	max-width: 100%;
	margin-top: ${(props) => props.$marginTop};
	margin-bottom: ${(props) => props.$marginBottom};
	padding: 0 ${(props) => props.$sidePadding};
	box-sizing: border-box;
	align-items: ${(props) => props.$align};
	justify-content: ${(props) => props.$justify};
	overflow: hidden;

	${(props) =>
		props.$compactMarginTop
			? `
		@media (max-height: 670px) {
			margin-top: ${props.$compactMarginTop};
		}
	`
			: ""}
`;

const Illustration = styled.img<{
	$width: string;
	$compactWidth?: string;
	$height: string;
	$maxWidth: string;
	$maxHeight: string;
	$compactMaxHeight?: string;
	$fit: NonNullable<Props["imageFit"]>;
	$position: string;
	$transform: string;
}>`
	display: block;
	width: ${(props) => props.$width};
	max-width: ${(props) => props.$maxWidth};
	height: ${(props) => props.$height};
	max-height: ${(props) => props.$maxHeight};
	object-fit: ${(props) => props.$fit};
	object-position: ${(props) => props.$position};
	transform: ${(props) => props.$transform};
	transform-origin: bottom right;
	animation: ${revealFromBottom} 320ms ease-out;

	${(props) =>
		props.$compactWidth || props.$compactMaxHeight
			? `
		@media (max-height: 670px) {
			${props.$compactWidth ? `width: ${props.$compactWidth};` : ""}
			${props.$compactMaxHeight ? `max-height: ${props.$compactMaxHeight};` : ""}
		}
	`
			: ""}
`;

const IllustratedStepCard = ({
	title,
	description,
	imageAlt,
	imageSrc,
	background = ColorV2.surface.brown,
	reverseOrder = false,
	copyPadding,
	cardPadding = "24px",
	imageWidth = "390px",
	imageCompactWidth,
	imageHeight = "390px",
	imageMaxWidth = "100%",
	imageMaxHeight = "none",
	imageCompactMaxHeight,
	imageCompactMarginTop,
	imageFit = "contain",
	imagePosition = "center",
	imageTransform = "none",
	imageMarginTop = "24px",
	imageJustify = "flex-end",
	imageAlign = "flex-end",
	imageMarginBottom = "0",
	imageSidePadding = "0",
}: Props) => {
	return (
		<StepContent>
			<Card $background={background} $padding={cardPadding}>
				{reverseOrder ? (
					<>
						<IllustrationWrap
							$align={imageAlign}
							$compactMarginTop={imageCompactMarginTop}
							$justify={imageJustify}
							$marginBottom={imageMarginBottom}
							$marginTop={imageMarginTop}
							$sidePadding={imageSidePadding}
						>
							<Illustration
								$compactWidth={imageCompactWidth}
								$compactMaxHeight={imageCompactMaxHeight}
								$height={imageHeight}
								$fit={imageFit}
								$maxHeight={imageMaxHeight}
								$maxWidth={imageMaxWidth}
								$position={imagePosition}
								$transform={imageTransform}
								$width={imageWidth}
								alt={imageAlt}
								src={imageSrc}
							/>
						</IllustrationWrap>
						<Copy $padding={copyPadding}>
							<Text type="h3" weight="semibold">
								{title}
							</Text>
							<Text type="p" weight="regular">
								{description}
							</Text>
						</Copy>
					</>
				) : (
					<>
						<Copy $padding={copyPadding}>
							<Text type="h3" weight="semibold">
								{title}
							</Text>
							<Text type="p" weight="regular">
								{description}
							</Text>
						</Copy>
						<IllustrationWrap
							$align={imageAlign}
							$compactMarginTop={imageCompactMarginTop}
							$justify={imageJustify}
							$marginBottom={imageMarginBottom}
							$marginTop={imageMarginTop}
							$sidePadding={imageSidePadding}
						>
							<Illustration
								$compactWidth={imageCompactWidth}
								$compactMaxHeight={imageCompactMaxHeight}
								$height={imageHeight}
								$fit={imageFit}
								$maxHeight={imageMaxHeight}
								$maxWidth={imageMaxWidth}
								$position={imagePosition}
								$transform={imageTransform}
								$width={imageWidth}
								alt={imageAlt}
								src={imageSrc}
							/>
						</IllustrationWrap>
					</>
				)}
			</Card>
		</StepContent>
	);
};

export default IllustratedStepCard;
